<!--suppress ALL -->
<template>
  <div class="slider-carousel">
    <transition-group ref="slideWrapper" tag="div" :name="transitionName"
                      class="slides-group">
      <div v-for="(slide, index) in slides" :key="current" v-if="show && current === index"
           class="slide" :class="slide.className">
        <div class="slide-background"
             :style="styleSlide(slide.image)">
          <div class="content">
            <p v-if="slide.title" class="title is-1 animated fadeInUp delay-dot5s">{{ slide.title }}</p>
            <p v-if="slide.description" class="subtitle is-5 animated fadeInUp delay-1s">{{ slide.description }}</p>
            <div v-if="$scopedSlots.default || $slots.default" class="slot-wrp animated fadeInUp delay-1dot5s">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </transition-group>
    <div v-if="!autoPlay" class="button-group">
      <button class="button is-success is-medium is-rounded" aria-label="Previous slide" @click="slide(-1)">
        <i class="fas fa-chevron-left"></i>
      </button>
      <button class="button is-success is-medium is-rounded" aria-label="Next slide" @click="slide(1)">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop} from 'annotation';

@Component
export default class SliderCarousel extends Vue {

  @Prop({default: []})
  public slides: Array<any>;

  @Prop({default: true, type: Boolean})
  public autoPlay: boolean;

  @Prop({default: 3000, type: Number})
  public ms: number;

  public current = 0;

  public direction = 1;

  public transitionName = "fade";

  public show = false;

  public mounted() {
    this.show = true;

    if (this.autoPlay) {
      setInterval(() => {
        this.slide(1);
      }, this.ms);
    }
  }

  public slide(dir) {
    this.direction = dir;
    this.transitionName = dir === 1 ? "slide-next" : "slide-prev";

    let len = this.slides.length;
    this.current = (this.current + dir % len + len) % len;
  }

  public styleSlide(image: string) {
    return {
      background: `linear-gradient(180deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.45)),url('${image}') no-repeat center`
    }
  }
}
</script>

<style lang="scss">
@import "../../style/variables_function";

// Import modern fonts
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

.slider-carousel {
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  margin: 0;
  font-size: 50px;
  color: #fff;

  /* FADE IN */
  .fade-enter-active {
    transition: opacity 1s;
  }

  .fade-enter {
    opacity: 0;
  }

  /* GO TO NEXT SLIDE */
  .slide-next-enter-active,
  .slide-next-leave-active {
    transition: transform 0.5s ease-in-out;
  }

  .slide-next-enter {
    transform: translate(100%);
  }

  .slide-next-leave-to {
    transform: translate(-100%);
  }

  /* GO TO PREVIOUS SLIDE */
  .slide-prev-enter-active,
  .slide-prev-leave-active {
    transition: transform 0.5s ease-in-out;
  }

  .slide-prev-enter {
    transform: translate(-100%);
  }

  .slide-prev-leave-to {
    transform: translate(100%);
  }

  .slide {
    width: 100%;
    height: 100vh;
    position: absolute;
    top: 0;
    left: 0;
  }

  .slide-background {
    display: block;
    width: 100%;
    height: 100%;
    background-size: cover !important;

    @include mobile {
      height: calc(100% - 84px);
      p.subtitle {
        text-align: justify;
      }
    }
  }

  .content {
    bottom: 20%;
    max-width: 600px;
    width: 100%;
    left: 10%;
    position: absolute;

    span, p, i {
      color: white !important;
    }
    
    p.title {
      font-family: $font-display;
      font-size: 3.5rem !important;
      font-weight: 800;
      line-height: 1.1;
      letter-spacing: -0.02em;
      margin-bottom: $space-lg !important;
      text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    
    p.subtitle {
      font-family: $font-body;
      font-size: 1.25rem !important;
      font-weight: 400;
      line-height: 1.6;
      opacity: 0.9;
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    }

    @include mobile {
      padding: 1rem;
      position: relative;
      left: 0;
      display: flex;
      flex-direction: column;
      bottom: 0;
      height: 100%;
      align-items: center;
      justify-content: center;
      text-align: center;
      
      p.title {
        font-size: 2rem !important;
      }
      
      p.subtitle {
        font-size: 1rem !important;
      }
    }
  }

  .button-group {
    position: absolute;
    right: 10%;
    display: flex;
    flex-direction: column;
    bottom: 30%;
    user-select: none;

    .button {
      background: $modern-gradient;
      border: none;
      @include button-glow;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      
      i {
        color: white;
      }

      &:not(:last-child) {
        margin-bottom: 1rem;
      }
      
      &:hover {
        background: $modern-gradient-hover;
      }
    }
  }

  @include mobile {
    .slider-carousel {
      height: auto;
      min-height: 100vh;
    }
  }
}
</style>
